import { CodexEntryType, CodexFieldType, PointOfViewType } from "#/index"
import { LucideProps } from "lucide-react"
import {
  CircleUserRoundIcon,
  MapPinIcon,
  BriefcaseBusinessIcon,
  GlobeIcon,
  GitBranchIcon,
  FileIcon,
  ChartNoAxesGanttIcon,
  RectangleEllipsisIcon,
  SquareChevronDownIcon,
  BookTextIcon
} from "lucide-react"

export const codexBasicGroups = [
  {
    label: '概念/对象',
    type: CodexEntryType.Character,
    icon: (props: Omit<LucideProps, 'ref'>) => <span className="anticon"><CircleUserRoundIcon {...props} /></span>
  },
  {
    label: '研究场域',
    type: CodexEntryType.Location,
    icon: (props: Omit<LucideProps, 'ref'>) => <span className="anticon"><MapPinIcon {...props} /></span>
  },
  {
    label: '指标/工具',
    type: CodexEntryType.Object,
    icon: (props: Omit<LucideProps, 'ref'>) => <span className="anticon"><BriefcaseBusinessIcon {...props} /></span>
  },
  {
    label: '理论框架',
    type: CodexEntryType.Lore,
    icon: (props: Omit<LucideProps, 'ref'>) => <span className="anticon"><GlobeIcon {...props} /></span>
  },
  {
    label: '案例线索',
    type: CodexEntryType.Subplot,
    icon: (props: Omit<LucideProps, 'ref'>) => <span className="anticon"><GitBranchIcon {...props} /></span>
  },
  {
    label: '其他',
    type: CodexEntryType.Other,
    icon: (props: Omit<LucideProps, 'ref'>) => <span className="anticon"><FileIcon {...props} /></span>
  },
]

export const codexFieldGroups = [
  {
    label: 'Text',
    value: CodexFieldType.Text,
    icon: ({ size }: { size: number }) => <ChartNoAxesGanttIcon size={size} />
  },
  {
    label: 'Line',
    value: CodexFieldType.Line,
    icon: ({ size }: { size: number }) => <RectangleEllipsisIcon size={size} />
  },
  {
    label: 'Dropdown',
    value: CodexFieldType.Dropdown,
    icon: ({ size }: { size: number }) => <SquareChevronDownIcon size={size} />
  },
  {
    label: '资料引用',
    value: CodexFieldType.Codex,
    icon: ({ size }: { size: number }) => <BookTextIcon size={size} />
  }
]

interface ColorOption {
  id: string
  label: string
  color: string  // 背景色
  textColor: string  // 文字颜色
  description?: string
}


export const colorPalette: {
  light: ColorOption[]
  dark: ColorOption[]
} = {
  light: [
    { id: 'none', label: 'None', color: 'none', textColor: '#000000', description: '默认' },
    // { id: 'black', label: 'Black', color: '#000000', textColor: '#ffffff', description: '经典黑色' },
    { id: 'gray', label: 'Gray', color: '#9ca3af', textColor: '#1f2937', description: '中性灰色' },
    { id: 'brown', label: 'Brown', color: '#a16207', textColor: '#ffffff', description: '温暖棕色' },
    { id: 'orange', label: 'Orange', color: '#ea580c', textColor: '#ffffff', description: '活力橙色' },
    { id: 'yellow', label: 'Yellow', color: '#eab308', textColor: '#1f2937', description: '明亮黄色' },
    { id: 'green', label: 'Green', color: '#16a34a', textColor: '#ffffff', description: '自然绿色' },
    { id: 'blue', label: 'Blue', color: '#2563eb', textColor: '#ffffff', description: '信任蓝色' },
    { id: 'purple', label: 'Purple', color: '#9333ea', textColor: '#ffffff', description: '神秘紫色' },
    { id: 'pink', label: 'Pink', color: '#db2777', textColor: '#ffffff', description: '浪漫粉色' },
    { id: 'red', label: 'Red', color: '#dc2626', textColor: '#ffffff', description: '热情红色' }
  ],
  dark: [
    { id: 'none', label: 'None', color: 'none', textColor: '#000000', description: '默认' },
    // { id: 'black', label: 'Black', color: '#374151', textColor: '#f9fafb', description: '深空黑色' },
    { id: 'gray', label: 'Gray', color: '#6b7280', textColor: '#f9fafb', description: '烟雾灰色' },
    { id: 'brown', label: 'Brown', color: '#92400e', textColor: '#fef3c7', description: '巧克力棕色' },
    { id: 'orange', label: 'Orange', color: '#ea580c', textColor: '#ffedd5', description: '日落橙色' },
    { id: 'yellow', label: 'Yellow', color: '#ca8a04', textColor: '#fefce8', description: '琥珀黄色' },
    { id: 'green', label: 'Green', color: '#15803d', textColor: '#f0fdf4', description: '森林绿色' },
    { id: 'blue', label: 'Blue', color: '#1d4ed8', textColor: '#dbeafe', description: '深夜蓝色' },
    { id: 'purple', label: 'Purple', color: '#7e22ce', textColor: '#f3e8ff', description: '暮光紫色' },
    { id: 'pink', label: 'Pink', color: '#be185d', textColor: '#fce7f3', description: '莓果粉色' },
    { id: 'red', label: 'Red', color: '#b91c1c', textColor: '#fef2f2', description: '宝石红色' }
  ]
}

export const povOptions: { id: PointOfViewType, label: string }[] = [
  { id: 'firstPerson', label: '第一人称' },
  { id: 'secondPerson', label: '第二人称' },
  { id: 'thirdPerson', label: '第三人称' },
  { id: 'thirdPersonLimited', label: '第三人称(有限)' },
  { id: 'thirdPersonOmniscient', label: '第三人称(全知)' }
]